/**
 * 公司基本資料設定 schema — REQ-20260817004。
 *
 * 「我方公司」的名稱 / 統編 / 地址 / 電話 / 傳真,登記一次,所有印得出我方抬頭的
 * 文件共用(月結對帳單、應收請款明細、藝品印刷訂單、進貨單)。
 * DB 只存一組(`system_setting` 的 key=`company.profile`),沒有另開資料表。
 *
 * **為什麼不繼續寄生在 `einvoice` 設定裡**:REQ-20260817003 當時為了印一組抬頭,
 * 把 `address` / `phone` / `fax` 塞進電子發票那個 blob,並在該檔 §六 寫明
 * 「日後真的要一組獨立的公司資料時,再獨立成 company_profile 設定」。
 * 現在就是那個時候 —— 公司基本資料是**每張單據都要用**的主檔級設定,
 * 而電子發票設定是「有沒有要開電子發票」的功能開關(還掛著 ezPay HashKey / HashIV
 * 這些機敏 / 內部欄位、以及 `@Roles('ADMIN')`)。把兩者綁在一起的結果是:
 * 沒開電子發票的人得進電子發票頁才能填公司地址。
 *
 * ⚠️ 電子發票的 `sellerName` / `sellerTaxId` **不搬也不刪**:那兩個欄位另有法規用途
 * (印在電子發票證明聯上),與這裡的顯示用抬頭
 * 是兩件事。這份設定沒填時的一次性 fallback 見 `CompanyProfileSettingsService`。
 */
import { z } from 'zod';

/** 使用者輸入(PUT /api/v1/settings/company-profile)。欄位長度沿用電子發票那份的限制 */
export const CompanyProfileSettingInputSchema = z.object({
  /** 公司名稱(印在單據抬頭) */
  name: z.string().trim().max(60).default(''),
  /** 統一編號(8 碼);允許留空 —— 沒填不該擋住整張單據列印 */
  taxId: z
    .string()
    .trim()
    .regex(/^\d{8}$/u, '統一編號須為 8 位數字')
    .or(z.literal('')),
  /** 公司地址 */
  address: z.string().trim().max(200).default(''),
  /** 公司電話 */
  phone: z.string().trim().max(50).default(''),
  /** 公司傳真 */
  fax: z.string().trim().max(50).default(''),
});
export type CompanyProfileSettingInput = z.infer<typeof CompanyProfileSettingInputSchema>;

/**
 * API 回傳(GET /api/v1/settings/company-profile)。
 * 這份設定沒有機敏欄位,所以與 Input 同形狀 —— 不像 SMTP / 電子發票要遮密碼與金鑰。
 */
export const CompanyProfileSettingViewSchema = z.object({
  name: z.string(),
  taxId: z.string(),
  address: z.string(),
  phone: z.string(),
  fax: z.string(),
});
export type CompanyProfileSettingView = z.infer<typeof CompanyProfileSettingViewSchema>;

/**
 * 請款 / 對帳 / 單據抬頭上的「我方公司」四個顯示欄位
 * (REQ-20260817003 建立,REQ-20260817004 隨資料來源一起搬到本檔)。
 *
 * 與上面那份 View 分開的唯一理由是**放行角色不同**:`GET /settings/company-profile`
 * 掛 `@Roles('ADMIN')`,而列印文件的畫面 ADMIN / SALES / VIEWER 都進得來,
 * 走的是 `GET /accounting/company-profile`。統編屬於設定頁的欄位,列印抬頭用不到,
 * 就不從那支窄端點吐出去。
 * 還沒有人填過時各欄位是空字串,呼叫端該留白而不是擋住整張文件。
 */
export const CompanyProfileSchema = z.object({
  name: z.string(),
  address: z.string(),
  phone: z.string(),
  fax: z.string(),
});
export type CompanyProfile = z.infer<typeof CompanyProfileSchema>;
